import { useEffect, useState } from "react";

import { jobRuns, type Job, type JobRun } from "@/lib/jobs";

import { runSummary, timestamp, verdictWord } from "./schedule";
import { Panel, PanelButton } from "./Setup";

/**
 * `Run history` — the outline button in the Jobs card header.
 *
 * Every job's runs in one list, newest first, because what you actually want to know is "what has
 * this thing been doing", not "what has *this job* been doing". The server keeps ~20 runs per job.
 */
export function RunHistory({ jobs, onClose }: { jobs: Job[]; onClose: () => void }) {
  const [rows, setRows] = useState<{ job: Job; run: JobRun }[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the ids and verdicts, not the array. `useJobs` replaces the list on every SSE frame,
  // so an identity-keyed effect fires a history request per job per progress tick — a request
  // storm for a panel whose contents only change when a run ENDS.
  const key = jobs.map((j) => `${j.id}:${j.state}:${j.lastRun?.id ?? 0}`).join("|");

  useEffect(() => {
    let cancelled = false;
    void Promise.all(jobs.map((job) => jobRuns(job.id).then(({ runs }) => ({ job, runs }))))
      .then((results) => {
        if (cancelled) return;
        setRows(
          results
            .flatMap(({ job, runs }) => runs.map((run) => ({ job, run })))
            .sort((a, b) => b.run.startedAt - a.run.startedAt),
        );
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <Panel
      title="実行履歴"
      subtitle={rows ? `${rows.length}件` : "読み込み中…"}
      actions={<PanelButton onClick={onClose}>閉じる</PanelButton>}
    >
      {error && <p className="px-[18px] py-4 text-secondary text-accent-hi">{error}</p>}
      {rows?.length === 0 && (
        <p className="px-[18px] py-4 text-secondary text-ink-3">まだ実行されたジョブはありません。</p>
      )}
      {rows?.map(({ job, run }) => (
        <div
          key={`${job.id}-${run.id}`}
          className="flex items-center gap-3 border-b border-line-soft px-4 py-2.5 last:border-b-0"
        >
          <span className="w-[124px] shrink-0 font-mono text-secondary text-ink-2">
            {timestamp(run.startedAt)}
          </span>
          <span className="w-[132px] shrink-0 text-secondary text-ink">{job.name}</span>
          <span
            className={[
              "w-[84px] shrink-0 text-secondary",
              run.verdict === "failed"
                ? "font-semibold text-accent-text"
                : run.verdict === "passed"
                  ? "text-ink-3"
                  : "text-ink-3",
            ].join(" ")}
          >
            {verdictWord(run.verdict)}
            {run.wasLate && <span className="text-ink-4">（遅延）</span>}
          </span>
          <span className="min-w-0 flex-1 truncate font-mono text-meta text-ink-4">
            {runSummary(run)}
          </span>
        </div>
      ))}
    </Panel>
  );
}
